import { ReactNode, useState } from "react";

import { Button } from "atoms/button/Button";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { TextField } from "atoms/text-field/TextField";

import OnboardingHeaderV3 from "../../components/OnboardingScreenHeader/OnboardingHeaderV3";

export interface LoginOrSignUpFormProps {
  initialPhoneNumber?: string;
  onSubmit: (phoneNumber: string) => Promise<void>;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hidePrimaryButton?: boolean;
  submitLabel?: string;
  consentRequired?: boolean;
  consentLabel?: string;
}

export const LoginOrSignUpForm = ({
  initialPhoneNumber = "",
  onSubmit,
  className = "",
  prefix,
  suffix,
  hidePrimaryButton = false,
  submitLabel = "Log in or sign up",
  consentRequired = false,
  consentLabel,
}: LoginOrSignUpFormProps) => {
  const [value, setValue] = useState(formatPhoneNumber(initialPhoneNumber));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(
    undefined
  );
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const handlePhoneNumberInput = (phone: string) => {
    const formattedPhoneNumber = formatPhoneNumber(phone);
    setValue(formattedPhoneNumber);
    // Clear error when user starts typing
    if (phoneNumberError) {
      setPhoneNumberError(undefined);
    }
  };

  const handleSubmit = async () => {
    const canSubmit = value.length >= 14;
    if (canSubmit) {
      try {
        setIsSubmitting(true);
        await onSubmit?.(value);
      } catch (err) {
        if (err instanceof Error) {
          setPhoneNumberError(err.message);
        } else {
          setPhoneNumberError("An error occurred. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyUp = (keyName: string) => {
    const canSubmit = value.length >= 14;
    if (keyName === "Enter" && canSubmit) {
      handleSubmit();
    }
  };

  const isValidPhoneNumber = value.length >= 14;

  return (
    <div className={`flex w-full max-w-sm flex-col ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? (
        <div className="mt-12 flex w-full items-center justify-center">
          {prefix}
        </div>
      ) : null}

      {/* Header */}
      <OnboardingHeaderV3
        title="Can we get your number?"
        align="center"
        className="mb-4"
      />

      <TextField
        type="tel"
        label="Enter your phone number"
        name="tel"
        autoComplete="tel"
        autoFocus
        onChange={handlePhoneNumberInput}
        value={value}
        pattern="[0-9]*"
        placeholder="(123) 456-7890"
        invalid={!!phoneNumberError}
        supportingText={phoneNumberError}
        onKeyUp={handleKeyUp}
        disabled={isSubmitting}
        className="!rounded-lg !border-0 !bg-white shadow-none !ring-0"
        labelClassName="!hidden"
      />

      {/* Optional consent checkbox for gated submission */}
      {consentRequired ? (
        <div className="mt-4">
          <div
            className="w-full"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if ((e.target as HTMLElement).tagName === "INPUT") return;
              const next = !isConsentChecked;
              setIsConsentChecked(next);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                const next = !isConsentChecked;
                setIsConsentChecked(next);
              }
            }}
          >
            <Checkbox
              label={
                consentLabel ||
                "By signing up, you agree to receive recurring automated promotional texts. Reply STOP to cancel, HELP for help. Message & data rates may apply. Consent is not required for purchase. See Terms & Privacy below."
              }
              checked={isConsentChecked}
              onChange={setIsConsentChecked}
              textStyle="text-body3"
            />
          </div>
        </div>
      ) : null}

      {/* Primary button */}
      {!hidePrimaryButton ? (
        <div className="mt-4">
          <Button
            fullWidth
            disabled={
              !isValidPhoneNumber ||
              isSubmitting ||
              (consentRequired && !isConsentChecked)
            }
            onClick={handleSubmit}
          >
            <span className="font-sans text-body1 font-normal">
              {isSubmitting ? "Loading..." : submitLabel}
            </span>
          </Button>
        </div>
      ) : null}

      {/* Optional suffix slot */}
      {suffix ? <div className="mt-6 w-full">{suffix}</div> : null}
    </div>
  );
};

const formatPhoneNumber = (inputValue: string) => {
  if (!inputValue) {
    return inputValue;
  }

  const phoneNumber = removeNonDigits(inputValue);

  if (phoneNumber.length < 4) {
    return phoneNumber;
  }

  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const removeNonDigits = (inputValue: string) =>
  inputValue.replace(/[^\d]/g, "");
